import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { useActiveLang } from "../../app/hooks/useActiveLang";
import { useApiInfiniteQuery, type Api } from "../../../services/api";

/** Podcast model as received from API (JSON-serialized, dates are `string`). */
export type ApiPodcastModel =
  Api["get"]["/webapp/public/podcasts/:id/detail"]["output"]["body"];

export type ApiPodcastEpisode = ApiPodcastModel["episodes"][number];

const limit = 20;

export function useGetPodcastsListQuery(
  roomId: string | null,
  params: {
    medicalSpecialties?: string;
    tags?: string;
  } = {}
) {
  const { contentLang } = useActiveLang();
  // Members-only (`guardRoom`). A reader who opened a shared podcast with no
  // session would only report a missing token to Sentry.
  const { isAuthenticated } = useCurrentUser();

  const medicalSpecialties =
    params.medicalSpecialties === undefined || params.medicalSpecialties === ""
      ? undefined
      : params.medicalSpecialties;
  const tags =
    params.tags === undefined || params.tags === "" ? undefined : params.tags;

  const query = useApiInfiniteQuery(
    "/webapp/private/podcasts",
    roomId === null || !isAuthenticated
      ? false
      : {
          query: {
            limit,
            language: contentLang,
            medicalSpecialties,
            tags,
            roomId,
          },
        }
  );

  const podcasts = query.state === "success" ? query.docs : [];

  return {
    podcasts,
    podcastIds: podcasts.map((podcast) => podcast.id).join(","),
    isLoading: query.state === "pending",
    /**
     * No listing to read: not asked for (no session, or a reader the page knows
     * cannot list the room), or answered with an
     * error, most often the refusal a non-member gets from `guardRoom`.
     */
    isListUnavailable: query.state === "idle" || query.state === "error",
    isFetching: query.state === "idle" ? false : query.isFetching,
    fetchNextPage:
      query.state === "success" ? query.fetchNextPage : () => void 0,
    canFetchNextPage: query.state === "success" && query.canFetchNextPage,
  };
}
